import type { OfferedValue } from "components/Document/ReviewInteraction";

import { safeParseFloat, safeParseInt } from "encamp-shared/src/tierii/utilities";
import { WasteManifestSource } from "generated-graphql/graphql";
import { SOURCE_LABEL, SOURCE_ORDER } from "routes/Customer/Waste/Manifests/manifestSourceDisplay";

/**
 * What e-Manifest and vendor data hold for the field the reviewer is editing,
 * ready to be offered under it.
 *
 * Only under the focused field. Sixty inputs each showing two other readings is
 * noise, and the reviewer can only act on the one they are in — the offer is
 * useful precisely because it appears where they are already looking.
 */

/** One source's value for one field, as the query returns it. */
export interface RawSourceValue {
  field: string;
  wasteLineNumber?: string | null;
  source: WasteManifestSource;
  /** As the reviewer reads it: a calendar date, a comma-separated list. */
  value: string;
  /**
   * As the form stores it, when that differs — `2026-01-05` where the reviewer
   * reads "Jan 5, 2026". Null when the two are the same.
   */
  formValue?: string | null;
}

/** Composite key for a per-line lookup: `${wasteLineNumber}:${field}`. */
function lineKey(wasteLineNumber: string, field: string): string {
  return `${wasteLineNumber}:${field}`;
}

export interface SourceOfferIndex {
  manifestOffers: ReadonlyMap<string, OfferedValue<string>[]>;
  wasteLineOffers: ReadonlyMap<string, OfferedValue<string>[]>;
  transporterOffers: ReadonlyMap<string, OfferedValue<string>[]>;
}

export const EMPTY_SOURCE_OFFERS: SourceOfferIndex = {
  manifestOffers: new Map(),
  wasteLineOffers: new Map(),
  transporterOffers: new Map(),
};

/** One value the document's own reading holds, as the query returns it. */
export interface RawReadingValue {
  field: string;
  wasteLineNumber?: string | null;
  transporterLineNumber?: string | null;
  value: string;
  formValue?: string | null;
  confidence?: number | null;
}

/** The label a reading chip carries, matching the extraction grid's wording. */
const READING_SOURCE = "This scan";

/** Where a pillar's value comes from, for the chip's tooltip. */
const SOURCE_PROVENANCE: Partial<Record<WasteManifestSource, string>> = {
  [WasteManifestSource.EManifest]: "What EPA's e-Manifest system holds for this manifest.",
  [WasteManifestSource.Vendor]: "What your vendor's data feed holds for this manifest.",
};

/**
 * How sure the model was, stated plainly, for hovering the chip that offers its
 * reading.
 *
 * Only on this chip. A pillar has no notion of confidence, and the field's own
 * value is the reviewer's once they have typed over it — a score describing a
 * value that is no longer there would be worse than none.
 */
function confidenceHint(confidence: number | null | undefined): string | undefined {
  if (confidence == null) return undefined;
  return `The AI was ${Math.round(confidence * 100)}% confident reading this.`;
}

/**
 * Where a reading came from, and how sure the model was of it.
 *
 * Provenance first: "This scan" is short enough to need spelling out, and a bare
 * percentage says nothing about whose value it describes.
 */
function readingHint(confidence: number | null | undefined): string {
  const source = "What the AI read from this document, before anyone edited it.";
  const hint = confidenceHint(confidence);
  return hint == null ? source : `${source} ${hint}`;
}

/**
 * Index the document's own reading alongside the pillars.
 *
 * First in the row, because it is the only offer that answers "what did the
 * document actually say" — the question a reviewer who has typed over a value has
 * when they want it back. The pillars are corroboration from elsewhere and read
 * after it.
 */
export function indexReadingOffers(rows: readonly RawReadingValue[]): SourceOfferIndex {
  const manifestOffers = new Map<string, OfferedValue<string>[]>();
  const wasteLineOffers = new Map<string, OfferedValue<string>[]>();
  const transporterOffers = new Map<string, OfferedValue<string>[]>();

  for (const row of rows) {
    const offer: OfferedValue<string> = {
      source: READING_SOURCE,
      display: row.value,
      value: row.formValue ?? row.value,
      tooltip: readingHint(row.confidence),
    };

    const [target, key] = (() => {
      if (row.wasteLineNumber != null) {
        return [wasteLineOffers, lineKey(row.wasteLineNumber, row.field)] as const;
      }
      if (row.transporterLineNumber != null) {
        return [transporterOffers, lineKey(row.transporterLineNumber, row.field)] as const;
      }
      return [manifestOffers, row.field] as const;
    })();

    const existing = target.get(key);
    if (existing) existing.push(offer);
    else target.set(key, [offer]);
  }

  return { manifestOffers, wasteLineOffers, transporterOffers };
}

/** Concatenate two indexes, keeping the first argument's offers ahead of the second's. */
export function mergeOfferIndexes(
  first: SourceOfferIndex,
  second: SourceOfferIndex
): SourceOfferIndex {
  const merge = (
    a: ReadonlyMap<string, OfferedValue<string>[]>,
    b: ReadonlyMap<string, OfferedValue<string>[]>
  ) => {
    const out = new Map<string, OfferedValue<string>[]>();
    for (const [key, offers] of a) out.set(key, [...offers]);
    for (const [key, offers] of b) {
      const existing = out.get(key);
      if (existing) existing.push(...offers);
      else out.set(key, [...offers]);
    }
    return out;
  };
  return {
    manifestOffers: merge(first.manifestOffers, second.manifestOffers),
    wasteLineOffers: merge(first.wasteLineOffers, second.wasteLineOffers),
    transporterOffers: merge(first.transporterOffers, second.transporterOffers),
  };
}

/**
 * Index the query's rows by the field they belong to.
 *
 * Sources come out in a stable order — the same one the sources-disagree tooltip
 * uses — so the keyboard shortcut for a given source does not move between
 * fields. A reviewer who learns that Alt+Enter takes e-Manifest should not find
 * it taking the vendor's value on the next field down.
 *
 * A source offering the same value as another still gets its own chip: agreement
 * between two sources is information, and collapsing them would hide it.
 */
export function indexSourceOffers(rows: readonly RawSourceValue[]): SourceOfferIndex {
  const manifestOffers = new Map<string, OfferedValue<string>[]>();
  const wasteLineOffers = new Map<string, OfferedValue<string>[]>();

  const sorted = [...rows].sort(
    (a, b) => SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source)
  );

  for (const row of sorted) {
    const offer: OfferedValue<string> = {
      source: SOURCE_LABEL[row.source],
      tooltip: SOURCE_PROVENANCE[row.source],
      display: row.value,
      // What the reviewer reads and what the field holds are not always the same
      // string. A date reads "Jan 5, 2026" and stores `2026-01-05`, and the
      // control is an `<input type="date">` that rejects the readable one — so
      // taking a date offer used to empty the field rather than fill it.
      value: row.formValue ?? row.value,
    };
    const target = row.wasteLineNumber != null ? wasteLineOffers : manifestOffers;
    const key = row.wasteLineNumber != null ? lineKey(row.wasteLineNumber, row.field) : row.field;
    const existing = target.get(key);
    if (existing) existing.push(offer);
    else target.set(key, [offer]);
  }

  // The pillars select no transporter columns, so there is nothing to file here
  // yet. Deferred deliberately: whether e-Manifest's "1" names the same carrier
  // as the document's needs answering before a value is offered under one.
  return { manifestOffers, wasteLineOffers, transporterOffers: new Map() };
}

/**
 * The offers for one form path, or none.
 *
 * Takes the form path rather than a field name so a caller needs to know nothing
 * about how repeated rows are keyed. The row's printed number comes from the
 * caller, because the form sorts rows and the sources do not, so a path's index
 * is not the number the offers are filed under.
 */
export function offersForField(
  index: SourceOfferIndex,
  field: string,
  wasteLineNumber?: string | null
): OfferedValue<string>[] {
  if (wasteLineNumber != null) {
    return index.wasteLineOffers.get(lineKey(wasteLineNumber, field)) ?? [];
  }
  return index.manifestOffers.get(field) ?? [];
}

/** The offers for one transporter's field, keyed by the line number the record holds. */
export function offersForTransporterField(
  index: SourceOfferIndex,
  field: string,
  transporterLineNumber: string
): OfferedValue<string>[] {
  return index.transporterOffers.get(lineKey(transporterLineNumber, field)) ?? [];
}

/**
 * Fields the manifest form holds as numbers, and how to read one back.
 *
 * Every offer carries a string, because that is what a chip displays and what
 * the sources arrive as. Most of the form is text and takes it unchanged, but
 * these two are typed `number | null`, and writing a string into them sends a
 * string to a `Float`/`Int` input — which fails variable coercion on save, so the
 * reviewer cannot commit until they retype the value by hand.
 *
 * The controls already parse their own typed input this way; this is the same
 * parse for a value that arrives by being taken rather than typed.
 */
const NUMERIC_FORM_FIELD: Record<string, (raw: string) => number | null> = {
  quantity: safeParseFloat,
  containerNumber: safeParseInt,
};

/**
 * An offered value in the terms the form field holds it.
 *
 * Keyed on the field name rather than the whole path, so a waste line's
 * `wasteLines.2.quantity` and a bare `quantity` resolve the same way.
 */
export function manifestFormValue(path: string, raw: string): string | number | null {
  const field = path.slice(path.lastIndexOf(".") + 1);
  const parse = NUMERIC_FORM_FIELD[field];
  if (parse == null) return raw;
  // A number the form cannot read is null rather than a string it will choke on
  // later: the field shows empty, which is visibly wrong, instead of failing at
  // save time with the value out of sight.
  return parse(raw);
}

/** Matches a repeated field path, e.g. `wasteLines.2.quantity`. */
const ROW_FIELD = /^(wasteLines|transporters)\.(\d+)\.(.+)$/;

/**
 * The offers for a form path.
 *
 * Takes the path so a caller needs to know nothing about how repeated rows are
 * keyed. The row's printed number comes from the form's own rendered order,
 * because the form sorts rows and the sources do not — a path's index is not the
 * number the offers are filed under. Transporters join the same way, on the line
 * number the record stores.
 */
export function manifestOffersForPath(
  index: SourceOfferIndex,
  path: string,
  rows: { wasteLines: readonly string[]; transporters?: readonly string[] }
): OfferedValue<string>[] {
  const row = ROW_FIELD.exec(path);
  if (row == null) return offersForField(index, path);

  const [, group, indexText, field] = row;
  const rowIndex = safeParseInt(indexText);
  if (rowIndex == null) return [];

  if (group === "transporters") {
    const transporterLineNumber = rows.transporters?.[rowIndex];
    if (transporterLineNumber == null) return [];
    return offersForTransporterField(index, field, transporterLineNumber);
  }

  const wasteLineNumber = rows.wasteLines[rowIndex];
  if (wasteLineNumber == null) return [];

  return offersForField(index, field, wasteLineNumber);
}
